import { BarChartIcon, BookIcon, BriefcaseIcon, CodeIcon, DatabaseIcon, FileTextIcon, GlobeIcon, LayoutDashboardIcon, LockIcon, PencilIcon, SpeakerIcon, UserIcon, VideoIcon, WrenchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { getPracticeInterviews } from "@service/interview";
import { InterviewItem } from "@type/interview";
import { catchErrorLog } from "@util/sentry";
import CategoryIcon from "@component/category-icon";
import InterviewCard from "@component/interview-card";
import BasicLayout from "@container/layout/basic";
import { useCompanySubdomain } from "@context/subdomain_context";
import { isSpaSubdomain } from "@util/subdomain";

export default function CandidateInterviewBrowsePage() {
    const [, companyInfo] = useCompanySubdomain();
    const isSpa = isSpaSubdomain();
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [practiceInterviews, setPracticeInterviews] = useState<InterviewItem[]>([]);
    const [isLoadingPractice, setIsLoadingPractice] = useState<boolean>(true);

    const fetchPracticeInterviews = async () => {
        try {
            setIsLoadingPractice(true);
            const response = await getPracticeInterviews();

            if (response.data) {

                // Update the practice interviews with their statuses
                // @ts-ignore
                const updatedPracticeInterviews = (response.data || []).map((interview) => {
                    const interviewContent = interview.content as any;
                    return {
                        id: interview.id,
                        alias: "",
                        title: interviewContent.interviewName || "",
                        description: interviewContent.description || "",
                        is_premium: false,
                        duration: interviewContent.maxDuration || 0,
                        company_name: interviewContent.aiName || "",
                        interview_type: "practice",
                        category: interviewContent.category || "",
                        created_at: interview.created_at,
                    };
                });

                setPracticeInterviews(updatedPracticeInterviews as InterviewItem[]);
            }
        } catch (error) {
            catchErrorLog(`failed to get practice interviews, err: ${error}`);
        } finally {
            setIsLoadingPractice(false);
        }
    };

    useEffect(() => {
        fetchPracticeInterviews()
    }, [])

    const categories = [
        { id: "software", label: "Software", icon: <CodeIcon size={20} /> },
        { id: "data-science", label: "Data", icon: <DatabaseIcon size={20} /> },
        { id: "finance", label: "Finance", icon: <BarChartIcon size={20} /> },
        { id: "product", label: "Product", icon: <LayoutDashboardIcon size={20} /> },
        { id: "business", label: "Business", icon: <BriefcaseIcon size={20} /> },
        { id: "consulting", label: "Consulting", icon: <UserIcon size={20} /> },
        { id: "writing", label: "Writing", icon: <FileTextIcon size={20} /> },
        { id: "design", label: "Design", icon: <PencilIcon size={20} /> },
        { id: "legal", label: "Legal", icon: <GlobeIcon size={20} /> },
        { id: "media", label: "Media", icon: <VideoIcon size={20} /> },
        { id: "engineering", label: "Engineering", icon: <WrenchIcon size={20} /> },
        { id: "statistics", label: "Statistics", icon: <BarChartIcon size={20} /> },
        { id: "marketing", label: "Marketing", icon: <SpeakerIcon size={20} /> },
        { id: "biology", label: "Biology", icon: <BookIcon size={20} /> },
        { id: "security", label: "Security", icon: <LockIcon size={20} /> },
        { id: "blockchain", label: "Blockchain", icon: <CodeIcon size={20} /> },
        { id: "soft-skill", label: "Soft Skill", icon: <GlobeIcon size={20} /> },
    ];

    const title = selectedCategory
        ? (
            <>
                <span className="capitalize">{selectedCategory}</span> mock interviews
            </>
        )
        : "Your perfect interview starts here";

    const filteredInterviews = practiceInterviews.filter((interview) => {
        if (selectedCategory === "") {
            return true;
        }
        return interview.category === selectedCategory;
    }).sort((a, b) => {
        return a.duration - b.duration;
    });

    const filterCategoryByInterviews = categories.filter((category) => {
        return category.label === practiceInterviews.find(interview => interview.category === category.label)?.category;
    })

    return (
        <BasicLayout showFooter={false}>
            <div className="py-6 px-4 mx-auto w-full">
                <section
                    className="py-12 md:py-16 text-center"
                    style={{ paddingBottom: "30px" }}
                >
                    <div className="container mx-auto px-4">
                        {/* SPA run their own simulation on their own subdomain, so
                            the page leads with their mark and their framing. The
                            hero is FIXED here — it does not swap to the
                            "<category> mock interviews" heading when a category is
                            picked, because this is the page's identity, not a
                            filter label, and the category chips below already show
                            what is selected. */}
                        {isSpa && companyInfo?.preferences?.company_logo && (
                            <div className="flex justify-center mb-6">
                                <img
                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo.preferences.company_logo}`}
                                    alt={companyInfo?.company_name || "Company logo"}
                                    className="h-16 md:h-24 w-auto object-contain"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                        )}
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4">
                            {isSpa ? "SPA Interview Simulation" : title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
                            {isSpa ? (
                                <>
                                    Practise with competency-based questions, receive structured
                                    feedback, and prepare with confidence.
                                </>
                            ) : (
                                <>
                                    Practice with expert-vetted interviews, get feedback on your
                                    performance, and land your dream opportunity.
                                </>
                            )}
                        </p>
                    </div>
                </section>

                {/* Categories */}
                {!isLoadingPractice && (
                    <div className="w-full overflow-x-auto py-4 flex justify-center">
                        <div className="flex gap-2 px-4 hover:text-blue-600 dark:hover:text-blue-400">
                            <CategoryIcon
                                icon={<VideoIcon size={20} />}
                                label="All Categories"
                                isActive={selectedCategory === ""}
                                onClick={() => setSelectedCategory("")}
                            />
                            {filterCategoryByInterviews.map((category) => (
                                <CategoryIcon
                                    key={category.id}
                                    icon={category.icon}
                                    label={category.label}
                                    isActive={selectedCategory === category.label}
                                    onClick={() => setSelectedCategory(category.label)}
                                />
                            ))}
                        </div>
                    </div>
                )}


                {/* Interview Cards */}
                <div className="w-full px-4 sm:px-8 py-6 min-h-screen">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                        {isLoadingPractice ? (
                            <>
                                {[...new Array(8)].map((_, index) => (
                                    <InterviewCard
                                        key={index}
                                        interview={null}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                {filteredInterviews.length > 0 ? (
                                    filteredInterviews.map((interview, index) => (
                                        <InterviewCard
                                            key={index}
                                            interview={interview}
                                        />
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-12">
                                        <p className="text-gray-500 dark:text-gray-400">
                                            No interviews found for this category. Please select another
                                            category.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </BasicLayout>
    )
}